import React from "react";
import { TriangleAlertIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MarginWarning,
  OtcFormWarnings,
  StepWarning,
} from "./warnings";
import {
  formatSignedUsd,
  stepWarningDialogText,
} from "./warnings-format";

export interface WarningConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warnings: OtcFormWarnings;
  isUpdateForm: boolean;
  onConfirm: () => Promise<void>;
  isSubmitting: boolean;
}

export function WarningConfirmDialog({
  open,
  onOpenChange,
  warnings,
  isUpdateForm,
  onConfirm,
  isSubmitting,
}: WarningConfirmDialogProps) {
  const hasMargin = warnings.margin.length > 0;
  const hasStep = warnings.step.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="otc-warning-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlertIcon className="h-5 w-5 text-orange-400" />
            Confirm contract review
          </DialogTitle>
          <DialogDescription>
            One or more values on this contract are outside the normal review
            ranges. Please confirm the highlighted fields before continuing.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto">
          {hasMargin && (
            <Card data-testid="otc-warning-margin-card">
              <CardHeader>
                <CardTitle className="text-base">
                  Margin amounts at or above $1,500
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-1 text-sm">
                  {warnings.margin.map((w: MarginWarning) => (
                    <li key={w.field} data-testid={`otc-warning-margin-${w.field}`}>
                      <span className="font-medium">{w.label}:</span>{" "}
                      {formatSignedUsd(w.value)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {hasStep && (
            <Card data-testid="otc-warning-step-card">
              <CardHeader>
                <CardTitle className="text-base">
                  Step values outside normal range
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-2 text-sm">
                  {warnings.step.map((w: StepWarning, idx: number) => (
                    <li
                      key={`${w.field}-${idx}`}
                      data-testid={`otc-warning-step-${w.field}-${w.level}`}
                    >
                      {stepWarningDialogText(w)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="otc-warning-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onConfirm();
            }}
            disabled={isSubmitting}
            data-testid="otc-warning-confirm"
          >
            {isUpdateForm ? "Confirm & Update" : "Confirm & Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
